import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { accountTypes } from 'config/user.type.constants'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAdminConfig, setAdminConfig } from 'services'
import { MultiSelect } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { workflowRestrictions } from '../Workflows/constants'

interface Condition {
  accountTypes: string[]
  loanStatuses: string[]
  restrictions: string[]
}

interface LoanOverviewAccessConfig {
  loanStructure: Condition[]
  loanApplication: Condition[]
  loanSubmission: Condition[]
}

const sections: { key: keyof LoanOverviewAccessConfig; label: string }[] = [
  { key: 'loanStructure', label: 'Loan Structure' },
  { key: 'loanApplication', label: 'Loan Application' },
  { key: 'loanSubmission', label: 'Loan Submission' },
]

const emptyCondition = (): Condition => ({ accountTypes: [], loanStatuses: [], restrictions: [] })

const normalizeConditions = (conditions: any[]): Condition[] =>
  (conditions || []).map((c: any) => ({
    accountTypes: c.accountTypes || [],
    loanStatuses: c.loanStatuses || [],
    restrictions: c.restrictions || [],
  }))

export const LoanOverviewAccess = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<LoanOverviewAccessConfig | null>(null)
  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])

  useEffect(() => {
    load()
  }, [])

  const load = () => {
    setLoading(true)
    getAdminConfig('loanOverviewAccess')
      .then((values) => {
        setData({
          loanStructure: normalizeConditions(values?.loanStructure),
          loanApplication: normalizeConditions(values?.loanApplication),
          loanSubmission: normalizeConditions(values?.loanSubmission),
        })
      })
      .finally(() => setLoading(false))
  }

  const save = async (updated: LoanOverviewAccessConfig) => {
    setLoading(true)
    await setAdminConfig('loanOverviewAccess', updated)
    setLoading(false)
  }

  const onChangeCondition = (
    section: keyof LoanOverviewAccessConfig,
    condIndex: number,
    field: keyof Condition,
    value: string[],
  ) => {
    if (!data) return
    const temp = cloneDeep(data)
    temp[section][condIndex][field] = value
    setData(temp)
  }

  const onAddCondition = async (section: keyof LoanOverviewAccessConfig) => {
    if (!data) return
    const temp = cloneDeep(data)
    temp[section].push(emptyCondition())
    setData(temp)
    await save(temp)
  }

  const onRemoveCondition = async (section: keyof LoanOverviewAccessConfig, condIndex: number) => {
    if (!data) return
    const temp = cloneDeep(data)
    temp[section].splice(condIndex, 1)
    setData(temp)
    await save(temp)
  }

  const onBlurSave = () => {
    if (data) save(data)
  }

  return (
    <div className="relative px-3 py-1.5">
      <LayoutLoading show={isLoading} />

      {data &&
        sections.map(({ key, label }, sectionIndex) => (
          <div className="mb-4" key={key}>
            <div className="text-xl font-bold mb-4">
              {sectionIndex + 1}. {label}
            </div>
            <RenderInput Key={key} input={{ inputType: 'section', title: 'Access Conditions' }} onChange={() => {}} />

            {data[key].map((condition, condIndex) => (
              <div className={`flex items-start gap-4 p-2 pl-4 ${condIndex % 2 ? 'bg-slate-100' : ''}`} key={condIndex}>
                <div className="flex-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <MultiSelect
                      id={`${key}-${condIndex}-accountTypes`}
                      title="Account Types"
                      options={accountTypes}
                      className="w-full"
                      defaultSelected={true}
                      value={condition.accountTypes}
                      onChange={(value) => onChangeCondition(key, condIndex, 'accountTypes', value)}
                      onBlur={onBlurSave}
                      sort
                      sortKey="value"
                    />
                    <MultiSelect
                      id={`${key}-${condIndex}-loanStatuses`}
                      title="Loan Statuses"
                      options={loanGlobalStatus}
                      className="w-full"
                      defaultSelected={true}
                      value={condition.loanStatuses}
                      onChange={(value) => onChangeCondition(key, condIndex, 'loanStatuses', value)}
                      onBlur={onBlurSave}
                    />
                    <RenderInput
                      Key={`${key}-${condIndex}-restrictions`}
                      input={{
                        inputType: 'multiselect',
                        title: 'Conditions',
                        options: workflowRestrictions,
                        value: condition.restrictions,
                        defaultSelected: false,
                        prefix: 'alphabet',
                      }}
                      onChange={(_: string, value: string[]) => onChangeCondition(key, condIndex, 'restrictions', value)}
                      onBlur={onBlurSave}
                    />
                  </div>
                </div>
                <button
                  className="mt-2 text-red-400 hover:text-red-600"
                  onClick={() => onRemoveCondition(key, condIndex)}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}

            <div className="pl-4 mt-2">
              <button
                className="flex items-center gap-1 text-shade-blue hover:opacity-80 text-sm"
                onClick={() => onAddCondition(key)}
              >
                <PlusIcon className="w-4 h-4" />
                Add Condition
              </button>
            </div>
          </div>
        ))}
    </div>
  )
}
